import { Navigate } from 'react-router';
import { useQuery } from '@tanstack/react-query';
import { useProductStore } from '@/stores/product';
import { usePermissions } from '@/hooks/usePermissions';
import { useAuthStore } from '@/stores/auth';
import { listFavorites, listDashboards } from '@/lib/bi/dashboard-api';
import type { DashboardFavorite } from '@/lib/bi/dashboard-api';

const PRIORITY_ROUTES = [
  { featureKey: 'get_started.setup_guide', path: '/setup' },
  { featureKey: 'analytics.dashboards', path: '/bi' },
  { featureKey: 'ingestion.data_sources', path: '/data-sources' },
  { featureKey: 'ingestion.pipelines', path: '/pipelines' },
  { featureKey: 'data_lake.catalog', path: '/catalog' },
  { featureKey: 'data_lake.explorer', path: '/queries' },
  { featureKey: 'scout.scout', path: '/ai' },
] as const;

export function DefaultRedirect() {
  const { branding } = useProductStore();
  const { can } = usePermissions();
  const userId = useAuthStore((s) => s.user?.id);
  const envId = useAuthStore((s) => s.user?.active_environment_id);

  // Check for default dashboard
  const { data: favorites, isLoading: favoritesLoading } = useQuery({
    queryKey: ['dashboard-favorites', userId, envId],
    queryFn: listFavorites,
    staleTime: 60_000,
    enabled: !!userId && !!envId,
  });

  // Also load accessible dashboards to validate the default still exists
  const { data: dashboards, isLoading: dashboardsLoading } = useQuery({
    queryKey: ['dashboards', userId, envId],
    queryFn: listDashboards,
    staleTime: 60_000,
    enabled: !!userId && !!envId,
  });

  // Wait for both queries to resolve before deciding where to redirect.
  // Without this, on fresh login (empty cache) both queries are undefined and the
  // component falls through to the fallback route before the data arrives.
  if (!!userId && !!envId && (favoritesLoading || dashboardsLoading)) {
    return null;
  }

  const defaultDashboard = favorites?.find((f: DashboardFavorite) => f.is_default);

  // Only redirect if the default dashboard is still accessible
  if (
    defaultDashboard &&
    can('analytics.dashboards') &&
    dashboards?.some((d) => d.id === defaultDashboard.dashboard_id)
  ) {
    return <Navigate to={`/bi/${defaultDashboard.dashboard_id}`} replace />;
  }

  // Product mode: go to dashboards if accessible, otherwise fall through to priority list
  if (branding && can('analytics.dashboards')) {
    return <Navigate to="/bi" replace />;
  }

  const first = PRIORITY_ROUTES.find(({ featureKey }) => can(featureKey));
  return <Navigate to={first?.path ?? '/setup'} replace />;
}
